import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Tooltip } from 'antd';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import { UploadOutlined } from '@ant-design/icons';
import 'antd/dist/antd.css';

import useNotification from './notification';

const type = 'DragableUploadList';

const DragableUploadListItem = ({ originNode, moveRow, file, fileList }) => {
  const ref = useRef();
  const index = fileList.indexOf(file);
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },
    drop: (item) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));
  const errorNode = <Tooltip title='Upload Error'>{originNode.props.children}</Tooltip>;
  return (
    <div
      ref={ref}
      className={`ant-upload-draggable-list-item ${isOver ? dropClassName : ''}`}
      style={{ cursor: 'move' }}>
      {file.status === 'error' ? errorNode : originNode}
    </div>
  );
};

const DragSortingUpload = ({ maxCount, onChange, fileList }) => {
  const [insideFileList, setInsideFileList] = useState([]);
  const { sendNotification } = useNotification();

  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {
      const dragRow = insideFileList[dragIndex];
      // console.log(dragRow);
      const newSort = update(insideFileList, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRow],
        ],
      });
      // console.log(newSort);
      onChange({ fileList: newSort });
      setInsideFileList(newSort);
    },
    [insideFileList]
  );

  useEffect(() => {
    // console.log(fileList, 'inside file list');
    fileList ? setInsideFileList(fileList) : setInsideFileList([]);
  }, [fileList]);

  const handleChange = (event) => {
    // const newFileList = event.fileList;
    // const newFile = event.file;
    // console.log(event);
    onChange(event);
    setInsideFileList(event.fileList);
  };

  const imageFileGuard = (file) => {
    const isImage = file.type === 'image/png' || file.type === 'image/jpeg';

    // check whether file is an image (.jpg, jpeg, .png)
    if (!isImage) {
      sendNotification({ msg: `${file.name} is not an image file type (.jpg, .png)`, variant: 'error' });
    }

    // check whether file is bigger than 2MB
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      sendNotification({ msg: 'Image must be smaller than 2MB!', variant: 'error' });
    }

    return (isImage && isLt2M) || Upload.LIST_IGNORE;
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Upload
        // action='https://www.mocky.io/v2/5cc8019d300000980a055e76'
        showUploadList={{ showPreviewIcon: false }}
        customRequest={({ file, onSuccess }) => {
          setTimeout(() => {
            onSuccess('ok');
          }, 200);
        }}
        beforeUpload={imageFileGuard}
        fileList={insideFileList}
        onChange={handleChange}
        maxCount={maxCount}
        itemRender={(originNode, file, currFileList) => (
          <DragableUploadListItem originNode={originNode} file={file} fileList={currFileList} moveRow={moveRow} />
        )}
        listType='picture-card'>
        {insideFileList.length < maxCount ? (
          <div>
            <UploadOutlined />
          </div>
        ) : null}
      </Upload>
    </DndProvider>
  );
};

export default DragSortingUpload;
